import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { switchMap, EMPTY, takeUntil, Subject, skip } from 'rxjs';
import { CurrentStateService } from '../../../../core/services/current-state.service';
import { ControlPoint } from '../../../../shared/models/controlPoint';
import { Site } from '../../../../shared/models/site';
import { ControlPointsService } from '../../services/control-points-service';
import { PopupCloseMode, PopupOpenMode } from '../../models/popupMode';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SeismographService } from '../../../seismograph-devices/services/seismograph-service';
import { Seismograph } from '../../../../shared/models/seismograph';
import { ControlPointInstrument } from '../../../../shared/models/controlPointInstrument';
import { PositionService } from '../../../../shared/services/utilities/position.service';
import { RecordHistoryComponent } from '../../../../shared/components/record-history/record-history.component';
import { VibrationsChart } from '../../../../shared/models/charts/VibrationsChart';
import { EventData } from '../../../vibration-events/models/event-data';
import { VibrationsRecordService } from '../../../../shared/services/records/vibrations-record.service';
import { TransformedCoords } from '../../../../shared/models/transformed-coords';
import { LocalStorageService } from '../../../../shared/services/utilities/local-storage.service';
import { RecordModalComponent } from '@shared/components/record-modal/record-modal.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DeviceStatusService } from '@shared/services/device-status/device-status.service';
import { DeviceStatus } from '@shared/models/device-status';

@Component({
  standalone: true,
  selector: 'app-slide-menu-control-points',
  imports: [CommonModule, AngularSvgIconModule, TranslateModule, RecordHistoryComponent],
  templateUrl: './slide-menu-control-points.component.html',
  styleUrl: './slide-menu-control-points.component.scss'
})
export class SlideMenuControlPointsComponent {

  getBatteryIcon(level: number): 'charging' | 'low' | 'medium' | 'battery' | 'full-battery' | 'normal' {
    if (level > 100) return 'charging';
    if (level < 20) return 'low';
    if (level < 50) return 'medium';
    if (level < 80) return 'battery';
    if (level <= 100) return 'full-battery';
    return 'normal';
  }
  @Input() toggle: boolean = false;
  @Input() currentSite: Site | undefined;
  private unsubscribe$ = new Subject<void>();
  private innerWidth: number | undefined;
  protected controlPoints: ControlPoint[] = [];
  protected subMenuVisible: boolean = false;
  protected menuVisible = true;
  protected btnCreateIsDisabled: boolean = false;
  protected btnPositionIsDisabled: boolean = false;
  protected lastSelectedCpIndex: number = -1;
  protected device: Seismograph | undefined;
  protected controlPointInstrumentsList: ControlPointInstrument[] = [];
  protected description: string = '';
  vibrationRecords: VibrationsChart[] = [];
  events: EventData[];
  controlPoint: ControlPoint;
  curentControlPointId: number;
  transformedCoords: TransformedCoords = { x: 0, y: 0 };
  DeviceStatus: DeviceStatus | undefined;


  constructor(
    protected currentStateService: CurrentStateService,
    private controlPointsHttpService: ControlPointsService,
    private router: Router,
    protected seismographService: SeismographService,
    private positionService: PositionService,
    private recordService: VibrationsRecordService,
    private cdr: ChangeDetectorRef,
    private localStorageService: LocalStorageService,
    private dialogRecorde: MatDialog,
    private deviceStatusService: DeviceStatusService,
    private translate: TranslateService

  ) { }

  ngOnInit() {

    this.innerWidth = window.innerWidth;

    // is trigger when site is selected from dropdown
    this.currentStateService.currentSite$
      .pipe(
        switchMap((site: Site | null) => {
          //always close sub menu with selected control point if new site is selected
          this.subMenuVisible = false;

          this.lastSelectedCpIndex = -1;

          // Only proceed if the site exists
          if (site) {
            this.currentSite = site;
            return this.controlPointsHttpService.getControlPointsBySiteId(site.siteId!, true);
          }
          // Return an empty observable if no site is present
          return EMPTY;
        })
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (points: ControlPoint[]) => {
          this.controlPoints = points;
        },
        error: err => {
          console.error('Error fetching control points:', err);
        }
      });

    // is trigger when ControlPoint is selected from list
    this.currentStateService.currentControlPoint$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (controlPoint: ControlPoint | null) => {
        if (controlPoint) {
          let index = this.controlPoints.findIndex(x => x.controlPointId === controlPoint.controlPointId);
          this.description = controlPoint.description;
          this.openCloseMenu(index);
        }
      }
    });

    // is trigger when popup is close
    this.currentStateService.closePopup$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (closeMode: PopupCloseMode | null) => {
        if (closeMode === PopupCloseMode.CREATED) {
          this.lastSelectedCpIndex = this.controlPoints.length - 1;
        }
        this.btnCreateIsDisabled = false;
        this.btnPositionIsDisabled = false;
      }
    });

    this.currentStateService.slideMenu$.pipe(skip(1)).subscribe({
      next: (isClose: boolean) => {
        this.menuVisible = isClose;
        this.closeAll();
      }
    });

    const controlPointIds: number[] = this.controlPoints.map(e => e.controlPointId);

    if (controlPointIds.length > 0) {
      (async () => {
        try {
          const controlPointInstruments: ControlPointInstrument[] =
            await this.seismographService.getSeismographsByControlPointIds(controlPointIds);

          this.controlPointInstrumentsList = controlPointInstruments;
        } catch (err) {
          console.error('Failed to load seismograph:', err);
        }
      })();
    }

    this.events = this.currentStateService.getCurrentEvents();

    setTimeout(() => {
      let currentControlPoint = this.currentStateService.getCurrentControlPoint();
      if (currentControlPoint) {
        this.lastSelectedCpIndex = this.controlPoints.findIndex(x => x.controlPointId === currentControlPoint.controlPointId);
        this.currentStateService.setCurrentControlPoint(currentControlPoint);
        this.selectControlPoint(currentControlPoint);
      }
    }, 200);

  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  protected toggleMenu() {
    this.menuVisible = !this.menuVisible;
    this.closeAll();
  }

  protected toggleSubMenu() {
    this.closeAll();
  }

  private closeAll() {
    if (!this.menuVisible) {
      this.lastSelectedCpIndex = -1;
      this.subMenuVisible = false;
      this.currentStateService.setClosePopup(PopupCloseMode.CLOSE);
      this.currentStateService.setCurrentControlPoint(null);
    }
  }

  protected openPopupCreate() {
    this.btnCreateIsDisabled = true;
    this.subMenuVisible = false;
    this.menuVisible = false;
    this.lastSelectedCpIndex = -1;
    this.currentStateService.setOpenPopup(PopupOpenMode.CREATE);
  }

  protected openPopupEditPosition() {
    if(this.btnPositionIsDisabled == true) return;
    this.btnCreateIsDisabled = true;
    this.btnPositionIsDisabled = true;
    this.currentStateService.setOpenPopup(PopupOpenMode.EDIT);
  }

  protected selectControlPoint(controlPoint: ControlPoint) {
    this.curentControlPointId = controlPoint.controlPointId;
    this.vibrationRecords = [];
    this.currentStateService.setClosePopup(PopupCloseMode.CLOSE);
    this.currentStateService.setCurrentControlPoint(controlPoint);
    this.loadDevice(controlPoint);
    this.controlPoint = controlPoint;
    this.loadVibrationRecords(controlPoint.controlPointId);

    if (this.innerWidth && this.innerWidth < 768) {
      this.menuVisible = false;
    }

    this.transformedCoords = {
      x: this.transformPosition(controlPoint).x,
      y: this.transformPosition(controlPoint).y
    }
    this.localStorageService.setItem('cp-transformedCoords', this.transformedCoords);


    this.cdr.detectChanges();
  }

  private async loadDevice(controlPoint: ControlPoint): Promise<void> {
    const existingInstrument = this.controlPointInstrumentsList.find(
      e => e.controlPointId === controlPoint.controlPointId
    );

    if (existingInstrument) {
      this.device = existingInstrument.seismograph;
    } else {
      try {
        const controlPointIds: number[] = controlPoint.controlPointId ? [controlPoint.controlPointId] : [];
        const controlPointInstruments = await this.seismographService.getSeismographsByControlPointIds(controlPointIds);
        if (controlPointInstruments.length > 0) {
          this.device = controlPointInstruments[0].seismograph;
          this.controlPointInstrumentsList.push(controlPointInstruments[0]);
        } else {
          this.device = undefined;
        }
      } catch (error) {
        console.error('Error loading device for control point:', error);
        this.device = undefined;
      }
    }

    if (this.device) {
      this.deviceStatusService.getDeviceStatus(this.device.serialNumber).subscribe(status => {
        this.DeviceStatus = status.data;
      });
    } else {
      this.DeviceStatus = undefined;
    }
  }



  protected openCloseMenu(controlPointIndex: number) {
    if (this.lastSelectedCpIndex == controlPointIndex) {
      this.lastSelectedCpIndex = -1;
      this.subMenuVisible = false;
    } else {
      this.lastSelectedCpIndex = controlPointIndex;

      if (!this.menuVisible) {
        this.menuVisible = true;
      }

      this.subMenuVisible = true;
    }
  }

  get borderRadiusStyle(): string {
    return this.subMenuVisible ? '0.5rem 0 0 0.5rem' : '0.5rem 0 0.5rem 0.5rem';
  }

  transformPosition(controlPoint: ControlPoint) {
    let transformedPosition = this.positionService.transforme(controlPoint.x, controlPoint.y, this.currentSite.epsg);
    return {
      x: transformedPosition.coordX,
      y: transformedPosition.coordY,
      z: controlPoint.z
    }
  }

  protected openControlPointDetail() {
    const controlPointId = this.currentStateService.getCurrentControlPoint()?.controlPointId;
    this.router.navigate(['/vibration-detail'], { queryParams: { siteId: this.currentSite?.siteId, controlPointId: controlPointId } });
  }

  loadVibrationRecords(controlPointId: number): void {
    this.recordService.GetRecords(this.currentSite.siteId, null,null, controlPointId,null,null,null,false,false,false)
      .subscribe({
        next: (response) => {
          this.vibrationRecords = response.data.records;
        },
        error: (err) => {
          console.error('Error loading vibration records', err);
        }
      });
  }
  onRecordClick(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      siteId: this.currentSite?.siteId,
      eventId: null,
      controlPointId: this.controlPoint?.controlPointId,
      siteName: this.currentSite?.nom,
      controlPointName: this.controlPoint?.name,
      regionName: this.currentSite?.region?.nom,
    };
    dialogConfig.enterAnimationDuration = '0ms';

    const dialogRef = this.dialogRecorde.open(RecordModalComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.loadVibrationRecords(this.controlPoint.controlPointId);
      }
    });
  }

            private getDeviceStatusInfo(): { isConnected: boolean, lastActive: string } {
              return {
                isConnected: !!this.DeviceStatus?.connected,
                lastActive: this.DeviceStatus?.minutesAgo != null && this.DeviceStatus?.connected ? this.formatMinutesAgo(this.DeviceStatus.minutesAgo) : '--'
              };
            }

          getStatusIcon(): string {
              const { isConnected } = this.getDeviceStatusInfo();
              return isConnected ? './icon/active.svg' : './icon/red.svg';
          }

          getStatusTitle(): string {
              const { isConnected } = this.getDeviceStatusInfo();
              return isConnected ? 'Connected' : 'Disconnected';
          }

            getStatusDateTitle(): string {
              const { isConnected, lastActive } = this.getDeviceStatusInfo();
              if (isConnected) {
                return lastActive;
              }
              const rawTimestamp = this.DeviceStatus?.timestamp ? String(this.DeviceStatus.timestamp).replace('T', ' ') : '--';
              return rawTimestamp === '--'? rawTimestamp: `${rawTimestamp} (local)`;
            }

            private formatMinutesAgo(minutesAgo: number): string {
              if (minutesAgo < 60) {
                return this.translate.instant('DEVICE_STATUS.MINUTES_AGO', { minutes: minutesAgo });
              }

              const hours = Math.floor(minutesAgo / 60);
              const minutes = minutesAgo % 60;

              return minutes > 0
                ? this.translate.instant(hours === 1 ? 'DEVICE_STATUS.HOUR_MINUTES_AGO' : 'DEVICE_STATUS.HOURS_MINUTES_AGO', { hours, minutes })
                : this.translate.instant(hours === 1 ? 'DEVICE_STATUS.HOUR_AGO' : 'DEVICE_STATUS.HOURS_AGO', { hours });
            }
}
